import { Component, computed, input, output, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { HlmPopoverImports } from '@spartan-ng/helm/popover';
import { HlmCalendar } from '@spartan-ng/helm/calendar';
import { formatDate } from 'utilities';
import { ThemedIconComponent } from '../../atoms/themed-icon/themed-icon.component';

@Component({
  selector: 'ocp-due-date-picker',
  imports: [TranslatePipe, HlmPopoverImports, HlmCalendar, ThemedIconComponent],
  templateUrl: './due-date-picker.component.html',
  styleUrl: './due-date-picker.component.scss',
  host: {
    class: 'due-date-picker',
  },
})
export class DueDatePickerComponent {
  /** Current date value */
  readonly value = input<Date | null>(null);

  /** Optional placeholder shown when no date is selected (overrides default 'Not set' text) */
  readonly placeholder = input<string>('');

  /** Accessible label for the trigger */
  readonly ariaLabel = input<string>('');

  /** Size (px) of the chevron icon — override per consumer without affecting other usages */
  readonly chevronSize = input<number>(7);

  /** Chevron icon filename — override per consumer without affecting other usages */
  readonly chevronIcon = input<string>('chevron-down.svg');

  /** Emits when a date is selected or cleared */
  readonly dateChange = output<Date | null>();

  /** Emits when the date picker popover is opened */
  readonly editorOpen = output<void>();

  /** Emits when the date picker popover is closed */
  readonly editorClose = output<void>();

  protected readonly overlayState = signal<'open' | 'closed' | null>(null);
  protected readonly isOpen = signal(false);

  /** Whether a valid date is set */
  protected readonly hasValue = computed<boolean>(() => {
    const d = this.value();
    return d instanceof Date && !Number.isNaN(d.getTime());
  });

  /** Formatted display string (YYYY-MM-DD) */
  protected readonly displayDate = computed(() => formatDate(this.value()));

  /** Safe value for the datepicker (null if invalid) */
  protected readonly safeValue = computed<Date | undefined>(() => {
    return this.hasValue() ? (this.value() ?? undefined) : undefined;
  });

  /** Minimum selectable date (today) */
  protected readonly minDate = new Date();

  protected onStateChanged(state: 'open' | 'closed'): void {
    if (state === 'closed') {
      this.overlayState.set(null);
    }
    this.isOpen.set(state === 'open');
    if (state === 'open') {
      this.editorOpen.emit();
    } else {
      this.editorClose.emit();
    }
  }

  onTriggerKeydown(event: KeyboardEvent): void {
    if (event.key === ' ') {
      event.preventDefault(); // prevent page scroll on Space
    }
  }

  onDateSelect(date: Date): void {
    // Normalize to UTC noon to avoid day-boundary timezone shifts
    const normalized = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0)
    );
    this.dateChange.emit(normalized);
    this.overlayState.set('closed');
  }

  onClear(): void {
    this.dateChange.emit(null);
    this.overlayState.set('closed');
  }

  onClearFromTrigger(event: Event): void {
    event.stopPropagation();
    if (event instanceof KeyboardEvent && event.key === ' ') {
      event.preventDefault();
    }
    this.dateChange.emit(null);
  }
}
